import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { RefreshCw, History, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function HistoricalRecordsModal({ 
  open, 
  onClose, 
  clientId, 
  clientName,
  onViewLedger
}) {
  const [loading, setLoading] = useState(false);
  const [mondayFinals, setMondayFinals] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (open && clientId) {
      fetchMondayFinals();
      setSelectedIds([]);
    }
  }, [open, clientId]);

  const fetchMondayFinals = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/settlements/monday-finals/${clientId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sort by date descending (newest first)
        const sorted = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMondayFinals(sorted);
      } else {
        toast.error('Failed to fetch historical records');
      }
    } catch (error) {
      console.error('Error fetching Monday Finals:', error);
      toast.error('Error loading historical records');
    } finally {
      setLoading(false);
    }
  };

  // Toggle selection of a Monday Final
  const toggleSelection = (mfId) => {
    setSelectedIds(prev => {
      if (prev.includes(mfId)) {
        return prev.filter(id => id !== mfId);
      } else {
        return [...prev, mfId];
      }
    });
  };

  // Select all
  const selectAll = () => {
    if (selectedIds.length === mondayFinals.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(mondayFinals.map(mf => mf.monday_final_id));
    }
  };

  const handleShow = () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one period to view');
      return;
    }
    
    // Pass selected IDs to parent to view combined ledger
    if (onViewLedger) {
      onViewLedger(selectedIds);
    }
    
    onClose(false);
  };

  const formatDate = (dateStr) => {
    try {
      const dt = new Date(dateStr);
      return format(dt, 'dd-MM-yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    try {
      const dt = new Date(dateStr);
      return format(dt, 'dd/MM/yy');
    } catch {
      return dateStr;
    }
  };

  // Get period range for each Monday Final (from previous MF to this MF)
  const getPeriodRange = (mf, index) => {
    const currentDate = formatDateShort(mf.created_at);
    
    // Find the next (older) Monday Final to get the start date
    const nextIndex = index + 1;
    if (nextIndex < mondayFinals.length) {
      const prevMF = mondayFinals[nextIndex];
      const prevDate = formatDateShort(prevMF.created_at);
      return `${prevDate} → ${currentDate}`;
    }
    
    // First Monday Final - show as "Start → date"
    return `Start → ${currentDate}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-purple-500" />
            Last Week Records - {clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : mondayFinals.length > 0 ? (
            <div className="flex flex-col h-full min-h-0">
              {/* Header with Select All */}
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <p className="text-sm text-muted-foreground">
                  Select periods to view combined ledger:
                </p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7"
                  onClick={selectAll}
                >
                  {selectedIds.length === mondayFinals.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              {/* Scrollable list with visible scrollbar */}
              <div 
                className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0 scrollbar-visible"
                style={{ maxHeight: '400px' }}
              >
                {mondayFinals.map((mf, index) => (
                  <div 
                    key={mf.monday_final_id}
                    className={`
                      flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors
                      ${selectedIds.includes(mf.monday_final_id) 
                        ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/20 dark:border-purple-700' 
                        : 'hover:bg-slate-50 dark:hover:bg-slate-900/50'
                      }
                    `}
                    onClick={() => toggleSelection(mf.monday_final_id)}
                    data-testid={`monday-final-item-${mf.monday_final_id}`}
                  >
                    <Checkbox 
                      checked={selectedIds.includes(mf.monday_final_id)}
                      onCheckedChange={() => toggleSelection(mf.monday_final_id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">
                          Monday Final: {formatDate(mf.created_at)}
                        </span>
                      </div>
                      
                      {/* Period range */}
                      <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        Period: {getPeriodRange(mf, index)}
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>Opening: <span className="font-mono">{mf.opening_balance?.toFixed(2)}</span></span>
                          <span>→</span>
                          <span>Closing: <span className="font-mono font-medium">{mf.closing_balance?.toFixed(2)}</span></span>
                        </div>
                        <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                          {mf.transaction_count} txns
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Selection summary */}
              {selectedIds.length > 0 && (
                <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded text-sm text-purple-700 dark:text-purple-300 flex-shrink-0">
                  {selectedIds.length} period(s) selected
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No historical records found</p>
              <p className="text-xs mt-1">Perform a Monday Final to create a historical snapshot</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleShow} 
            disabled={selectedIds.length === 0 || loading}
            className="gap-1"
          >
            <Eye className="h-4 w-4" />
            View Ledger ({selectedIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
